import { countryName, isoAlpha2 } from "./countryName";
import { hasFlag } from "./flagCodes";
import styles from "./CountryCell.module.css";

interface CountryCellProps {
  /**
   * Two-letter ISO code, or null for a row GeoIP never placed — the normal
   * answer for a project running with GeoIP off, and for an account that
   * predates the column and has not signed in since.
   */
  code: string | null | undefined;
  /**
   * `code` — flag + ISO code, for a table cell.
   * `name` — a slightly larger flag + the country's name, for an identity card.
   * `flag` — a small flag alone, named in its tooltip, for a cell too narrow
   * for the code; nothing at all when there is no country to draw.
   */
  variant?: "code" | "name" | "flag";
}

/**
 * A row's country: the flag next to its code (lists) or next to its name
 * (cards), and a muted dash when there is none.
 */
export function CountryCell({ code, variant = "code" }: CountryCellProps) {
  if (!code) {
    return variant === "flag" ? null : <span className={styles.none}>—</span>;
  }
  // Anything that is not two letters is not a country this panel can name or
  // draw, whatever the column allowed — it stays on screen as the raw code.
  const iso = isoAlpha2(code);
  const flagClass = iso && hasFlag(iso) ? `fi fi-${iso.toLowerCase()}` : null;
  if (variant === "flag") {
    if (!flagClass) return null;
    return (
      <span className={`${styles.country} ${styles.sm}`} title={countryName(code)}>
        <span className={`${flagClass} ${styles.flag}`} aria-hidden="true" />
      </span>
    );
  }
  const isName = variant === "name";
  return (
    <span className={`${styles.country} ${isName ? styles.lg : ""}`}>
      {flagClass && (
        <span className={`${flagClass} ${styles.flag}`} aria-hidden="true" />
      )}
      <span className={isName ? styles.name : styles.code}>
        {isName ? countryName(code) : (iso ?? code)}
      </span>
    </span>
  );
}
